import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Megaphone, Bell, Star, FolderSearch, ArrowRight, Lock, Rss, FileText, MessagesSquare, NotebookPen, Video, Clock, CreditCard, Loader2, Flame, Trophy, Search, UserPlus, Youtube, Play, Copy, Check, Gift, Crown, Medal, Award, Zap, ListChecks, Trash2, Radio, AlertTriangle, Mail, Share2, Facebook, Twitter, ChevronDown } from "lucide-react";

const YT_CHANNEL = "https://www.youtube.com/@helpsolvethecase?sub_confirmation=1";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { PostImages } from "@/components/ImageUploader";
import { caseDisplayName } from "@/lib/caseName";

function AnnouncementComments({ ann, user }) {
  const isMember = user && (user.role === "admin" || user.subscription_status === "active");
  const isStaff = user && (user.role === "admin" || user.role === "hstc_assistant");
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [count, setCount] = useState(ann.comment_count || 0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => api.get(`/announcements/${ann.ann_id}/comments`)
    .then(({ data }) => { setComments(data); setCount(data.length); }).catch(() => setComments([]));
  const toggle = () => { const n = !open; setOpen(n); if (n && comments === null) load(); };
  const submit = async () => {
    const b = text.trim(); if (!b || sending) return; setSending(true);
    try { await api.post(`/announcements/${ann.ann_id}/comments`, { body: b }); setText(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const del = async (c) => { if (!window.confirm("Delete this comment?")) return; await api.delete(`/announcement-comments/${c.comment_id}`).catch(() => {}); load(); };
  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <button onClick={toggle} data-testid={`ann-comments-toggle-${ann.ann_id}`}
        className="flex items-center gap-1.5 text-xs text-white/50 hover:text-gold transition-colors">
        <MessagesSquare className="w-3.5 h-3.5" /> {count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Comment"}
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid={`ann-comments-${ann.ann_id}`}>
          {comments === null ? (
            <div className="text-xs text-white/40 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-xs text-white/40">No comments yet.{isMember ? " Be the first to reply." : ""}</div>
          ) : comments.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-2 group" data-testid={`ann-comment-${c.comment_id}`}>
              <div className="w-7 h-7 rounded-full bg-gold/15 border border-gold/25 flex items-center justify-center text-[11px] font-bold text-gold shrink-0">
                {(c.author_name || "M").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-white/90">{c.author_name}</span>
                  {["admin", "hstc_assistant"].includes(c.author_role) && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-gold/80 text-charcoal font-bold">HSTC</span>}
                  <span className="text-[10px] text-white/30">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                  {(isStaff || (user && c.author_id === user.user_id)) && (
                    <button onClick={() => del(c)} data-testid={`del-ann-comment-${c.comment_id}`} className="ml-auto opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition-all"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
                {c.body && <p className="text-sm text-white/70 whitespace-pre-line mt-0.5">{c.body}</p>}
                <PostImages images={c.images} />
              </div>
            </div>
          ))}
          {isMember ? (
            <div className="flex items-end gap-2 pt-1">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
                placeholder="Write a comment…" data-testid={`ann-comment-input-${ann.ann_id}`}
                className="flex-1 resize-none bg-charcoal border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50" />
              <button onClick={submit} disabled={sending || !text.trim()} data-testid={`ann-comment-submit-${ann.ann_id}`}
                className="px-3 py-2 text-xs font-semibold bg-gold text-charcoal rounded-md hover:bg-gold-hover disabled:opacity-40 transition-colors">Post</button>
            </div>
          ) : (
            <div className="text-xs text-white/40">Become a member to join the conversation.</div>
          )}
        </div>
      )}
    </div>
  );
}

function AnnouncementCard({ a, user }) {
  const [expanded, setExpanded] = useState(false);
  const longText = (a.body || "").length > 160;
  return (
    <div data-testid={`announcement-${a.ann_id}`} className="card-surface rounded-md p-4 border-l-2 border-l-gold">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-gold/90 text-charcoal font-bold">HSTC</span>
        <span className="label-mono text-white/40 text-[11px]">{a.author || "Christine Burke"}{a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString()}` : ""}</span>
      </div>
      <h3 className="font-display text-base font-semibold text-white leading-snug">{a.title}</h3>
      <p className={`mt-1 text-sm text-white/65 leading-relaxed whitespace-pre-line ${expanded ? "" : "line-clamp-2"}`}>{a.body}</p>
      {longText && (
        <button onClick={() => setExpanded(!expanded)} data-testid={`ann-readmore-${a.ann_id}`} className="mt-1 text-xs font-semibold text-gold hover:underline">
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      <PostImages images={a.images} compact />
      <AnnouncementComments ann={a} user={user} />
    </div>
  );
}



function StreamTodo() {
  const [todos, setTodos] = useState([]);
  const [text, setText] = useState("");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("stream_todo_collapsed") === "1");
  const load = () => api.get("/stream-todos").then(({ data }) => setTodos(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("stream_todo_collapsed", c ? "0" : "1"); return !c; });
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    try { await api.post("/stream-todos", { text: t }); setText(""); load(); }
    catch { toast.error("Could not add"); }
  };
  const toggle = async (todo) => {
    try { await api.put(`/stream-todos/${todo.todo_id}`, { done: !todo.done }); load(); }
    catch { toast.error("Could not update"); }
  };
  const del = async (todo) => {
    try { await api.delete(`/stream-todos/${todo.todo_id}`); load(); }
    catch { toast.error("Could not delete"); }
  };
  const remaining = todos.filter((t) => !t.done).length;

  return (
    <div className="card-surface rounded-md p-6 mb-8 border-gold/30" data-testid="stream-todo">
      <button onClick={toggleCollapse} className="w-full flex items-center justify-between gap-3 mb-4" data-testid="stream-todo-collapse">
        <div className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-gold" /><h2 className="label-mono">Stream To-Do</h2></div>
        <div className="flex items-center gap-3">
          <span className="label-mono text-white/50">{remaining} to go</span>
          <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
        </div>
      </button>
      {!collapsed && (
        <>
          <div className="flex gap-2 mb-4">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="Add a to-do for your next stream…"
              className="flex-1 bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none"
              data-testid="stream-todo-input"
            />
            <button onClick={add} className="px-4 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm" data-testid="stream-todo-add">Add</button>
          </div>
          {todos.length === 0 ? (
            <p className="text-sm text-white/40">No stream tasks yet — add your first above.</p>
          ) : (
            <div className="space-y-1.5">
              {todos.map((t) => (
                <div key={t.todo_id} className="flex items-center gap-3 group py-1.5 border-b border-white/5 last:border-0" data-testid={`stream-todo-item-${t.todo_id}`}>
                  <button onClick={() => toggle(t)} className={`shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${t.done ? "bg-gold border-gold" : "border-white/25 hover:border-gold/60"}`} data-testid={`stream-todo-toggle-${t.todo_id}`}>
                    {t.done && <Check className="w-3.5 h-3.5 text-charcoal" />}
                  </button>
                  <span className={`flex-1 text-sm ${t.done ? "text-white/35 line-through" : "text-white/85"}`}>{t.text}</span>
                  <button onClick={() => del(t)} className="shrink-0 text-white/25 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`stream-todo-del-${t.todo_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GoLive() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "We're live now", message: "", link: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.link.trim()) { toast.error("Paste the join link first"); return; }
    if (!window.confirm("Send a LIVE alert (email + notification) to all active members now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/go-live", form);
      toast.success(`Live alert sent to ${data.recipients} member${data.recipients === 1 ? "" : "s"}`);
      setOpen(false);
      setForm({ title: "We're live now", message: "", link: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="card-surface rounded-md p-6 mb-8 border-red-500/30" data-testid="go-live-card">
      <div className="flex items-center justify-between gap-3 flex-col sm:flex-row">
        <div className="flex items-start gap-2">
          <Radio className="w-4 h-4 text-red-400 mt-0.5" />
          <div>
            <h2 className="label-mono">Go Live <span className="text-white/40">· only visible to you</span></h2>
            <p className="text-sm text-white/50 mt-1">Instantly alert all active members by email + in-app notification that you're live. You confirm the join link each time.</p>
          </div>
        </div>
        {!open ? (
          <button onClick={() => setOpen(true)} data-testid="go-live-open-btn" className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white font-semibold rounded-sm hover:bg-red-600 transition-colors text-sm">
            <Radio className="w-4 h-4" /> Go Live
          </button>
        ) : (
          <button onClick={() => setOpen(false)} className="shrink-0 text-sm text-white/50 hover:text-white">Cancel</button>
        )}
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline (e.g. Christine is live in the Bullpen)" data-testid="go-live-title-input" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-gold/50 outline-none" />
          <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Paste the join link (Zoom, YouTube, etc.)" data-testid="go-live-link-input" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-gold/50 outline-none" />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Optional short message" rows={2} data-testid="go-live-message-input" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-gold/50 outline-none" />
          <button onClick={send} disabled={busy} data-testid="go-live-send-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white font-semibold rounded-sm hover:bg-red-600 transition-colors text-sm disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} {busy ? "Sending…" : "Send live alert to all members"}
          </button>
        </div>
      )}
    </div>
  );
}

function AdminSignupNote() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/signups").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const realMembers = (data?.recent_members || []).filter((m) => !m.is_test);
  const realCount = data?.counts?.real ?? realMembers.length;
  const newThisWeek = realMembers.filter((m) => m.created_at && (Date.now() - new Date(m.created_at).getTime()) < 7 * 86400 * 1000).length;

  return (
    <div className="card-surface rounded-md p-6 mb-8 border-gold/20" data-testid="admin-signup-note">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-gold" /><h2 className="label-mono">New Registrations</h2></div>
        <div className="label-mono">
          <span className="text-green-400">{realCount} real member{realCount === 1 ? "" : "s"}</span>
          {newThisWeek > 0 && <span className="text-white/50"> · {newThisWeek} this week</span>}
        </div>
      </div>
      {realMembers.length === 0 ? (
        <p className="text-sm text-white/40">No real registrations yet. New members who sign up will appear here.</p>
      ) : (
        <div className="space-y-2">
          {realMembers.slice(0, 5).map((m, i) => (
            <div key={m.email || i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0" data-testid={`admin-signup-${i}`}>
              <div className="min-w-0">
                <span className="text-white font-medium truncate">{m.name || m.email}</span>
                <div className="text-xs text-white/40 font-mono truncate">{m.email}</div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${m.subscription_status === "active" ? "bg-green-400/15 text-green-400" : "bg-white/10 text-white/50"}`}>{m.subscription_status === "active" ? (m.subscription_plan || "member") : "free"}</span>
                <div className="text-[10px] text-white/30 font-mono mt-0.5">{timeAgo(m.created_at)}</div>
              </div>
            </div>
          ))}
          <Link to="/members" data-testid="admin-view-all-signups" className="inline-flex items-center gap-1 pt-1 label-mono text-gold hover:underline">See all members <ArrowRight className="w-3 h-3" /></Link>
        </div>
      )}
    </div>
  );
}

function CaseSearchBar() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/public/cases").then(({ data }) => setCases(Array.isArray(data) ? data : (data.cases || []))).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const term = q.trim().toLowerCase();
  const results = term
    ? cases.filter((c) => c.title.toLowerCase().includes(term) || (c.location || "").toLowerCase().includes(term)).slice(0, 8)
    : [];

  const go = (c) => { if (!c) return; setOpen(false); setQ(""); navigate(`/cases/${c.slug}`); };

  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active] || results[0]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative mb-8" data-testid="dashboard-case-search">
      <div className="flex items-center gap-3 card-surface rounded-md px-4 focus-within:border-gold/40 transition-colors">
        <Search className="w-4 h-4 text-white/40 shrink-0" />
        <input
          data-testid="dashboard-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search cases by name or location…"
          className="w-full bg-transparent py-3.5 text-white placeholder:text-white/40 focus:outline-none text-sm"
        />
        {q && <button data-testid="dashboard-search-clear" onClick={() => { setQ(""); setOpen(false); }} className="label-mono text-white/40 hover:text-gold shrink-0">Clear</button>}
      </div>
      {open && term && (
        <div className="absolute z-20 left-0 right-0 mt-2 card-surface rounded-md overflow-hidden shadow-2xl" data-testid="dashboard-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-white/40">No cases match “{q}”.</div>
          ) : results.map((c, i) => (
            <button
              key={c.case_id}
              data-testid={`search-result-${c.slug}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(c)}
              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 border-b border-white/5 last:border-0 transition-colors ${i === active ? "bg-gold/10" : "hover:bg-white/5"}`}
            >
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{caseDisplayName(c.title)}</div>
                {c.location && <div className="text-xs text-white/40 font-mono truncate">{c.location}</div>}
              </div>
              <ArrowRight className="w-4 h-4 text-gold shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LatestVideos({ channelVids, caseVids }) {
  const [playing, setPlaying] = useState(null);
  const caseByYt = {};
  (caseVids || []).forEach((v) => { if (v.youtube_id) caseByYt[v.youtube_id] = v; });
  let list = (channelVids && channelVids.length)
    ? channelVids
    : (caseVids || []).map((v) => ({ youtube_id: v.youtube_id, title: v.title, thumbnail: `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`, published: v.published }));
  if (!list.length) return null;
  return (
    <section data-testid="latest-videos-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2"><Youtube className="w-4 h-4 text-[#FF4D4D]" /><h2 className="label-mono">Latest Videos</h2></div>
        <a href={YT_CHANNEL} target="_blank" rel="noreferrer" data-testid="yt-subscribe-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#FF0000] text-white text-xs font-semibold hover:opacity-90 transition-opacity">
          <Youtube className="w-3.5 h-3.5" /> Subscribe
        </a>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 px-1 -mx-1" data-testid="latest-videos-strip">
        {list.slice(0, 12).map((v) => {
          const cv = caseByYt[v.youtube_id];
          const thumb = v.thumbnail || `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`;
          return (
            <div key={v.youtube_id} className="w-72 shrink-0 card-surface rounded-md overflow-hidden" data-testid={`video-${v.youtube_id}`}>
              <div className="relative aspect-video bg-black">
                {playing === v.youtube_id ? (
                  <iframe title={v.title} className="w-full h-full" src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1&rel=0`} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
                ) : (
                  <button onClick={() => setPlaying(v.youtube_id)} data-testid={`play-${v.youtube_id}`} className="group w-full h-full block">
                    <img src={thumb} alt={v.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-11 h-11 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg"><Play className="w-5 h-5 text-white fill-current ml-0.5" /></div>
                    </div>
                  </button>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-white leading-snug" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{v.title}</p>
                {cv ? (
                  <Link to={`/cases/${cv.case_slug}`} data-testid={`video-discuss-${v.youtube_id}`} className="text-xs text-gold/80 hover:text-gold mt-1 inline-flex items-center gap-1 max-w-full truncate">Discuss on {caseDisplayName(cv.case_title)} <ArrowRight className="w-3 h-3 shrink-0" /></Link>
                ) : (
                  <span className="text-xs text-white/40 font-mono mt-1 inline-block">{(v.published || "").slice(0, 10)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}



function MessageRow({ m }) {
  return (
    <Link to={m.link} data-testid={`recent-msg-${m.thread_id}`} className="group flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors">
      <MessagesSquare className={`w-4 h-4 mt-0.5 shrink-0 ${m.type === "comment" ? "text-blue-300" : "text-green-400"}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white truncate">{m.author_name}</span>
          {m.author_role === "admin" && <span className="text-[9px] font-mono px-1 py-0.5 rounded-sm bg-gold/15 text-gold shrink-0">DET</span>}
          <span className="text-[10px] font-mono text-white/30 ml-auto shrink-0">{timeAgo(m.created_at)}</span>
        </div>
        <p className="text-sm text-white/70 mt-0.5 break-words">{m.snippet}</p>
        <div className="flex items-center gap-3 mt-1.5">
          {m.following && <Star className="w-3 h-3 text-gold fill-current shrink-0" />}
          <span className="text-xs text-white/40 truncate">{m.type === "comment" ? "replied in" : "posted in"} <span className="text-gold/80">{caseDisplayName(m.case_title)}</span></span>
          <span className="ml-auto flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1 text-xs text-white/40" title={`${m.reply_count} replies`}>
              <MessagesSquare className="w-3 h-3" />{m.reply_count ?? 0}
            </span>
            <span className="flex items-center gap-1 text-xs font-mono text-gold group-hover:underline" data-testid={`reply-cta-${m.thread_id}`}>
              Reply <ArrowRight className="w-3 h-3" />
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}

const BADGE_ICON = { shield: Award, "user-plus": UserPlus, users: UserPlus, message: MessagesSquare, heart: Star, flame: Flame };

function ReferralCard() {
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/me/referral").then(({ data }) => setRef(data)).catch(() => {}); }, []);
  if (!ref) return null;
  const link = `${window.location.origin}${ref.path}`;
  const shareMsg = "I'm following true-crime cases on Help Solve The Case — the case files, timelines and detective notes all in one place. Join me:";
  const copy = () => {
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true); toast.success("Invite link copied!"); setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Couldn't copy — select and copy manually."));
  };
  const shareUrls = {
    sms: `sms:?&body=${encodeURIComponent(`${shareMsg} ${link}`)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMsg)}&url=${encodeURIComponent(link)}`,
  };
  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");
  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: "Help Solve The Case", text: shareMsg, url: link }).catch(() => {});
    } else {
      copy();
    }
  };
  return (
    <section className="card-surface rounded-md p-5" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-gold" /><h2 className="label-mono">Invite &amp; Earn Free Membership</h2></div>
      <p className="text-sm text-white/60 leading-relaxed">
        Every <span className="text-gold font-semibold">{ref.referrals_per_free_month} people</span> you invite who become Founding Members earns you a <span className="text-gold font-semibold">free month</span> — automatically. Keep going and you ride free.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input readOnly value={link} data-testid="referral-link-input" onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 bg-charcoal/60 border border-white/10 rounded-sm px-3 py-2 text-xs font-mono text-white/70 focus:outline-none focus:border-gold/40" />
        <button onClick={copy} data-testid="referral-copy-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-gold text-charcoal text-xs font-semibold rounded-sm hover:bg-gold-hover transition-colors">
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="referral-share-row">
        <span className="label-mono text-white/40 mr-0.5">Share:</span>
        <button onClick={() => openShare(shareUrls.sms)} data-testid="share-text-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 text-white/80 text-xs hover:border-gold/40 hover:text-gold transition-colors">
          <MessagesSquare className="w-3.5 h-3.5" /> Text
        </button>
        <button onClick={() => openShare(shareUrls.facebook)} data-testid="share-facebook-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 text-white/80 text-xs hover:border-gold/40 hover:text-gold transition-colors">
          <Facebook className="w-3.5 h-3.5" /> Facebook
        </button>
        <button onClick={() => openShare(shareUrls.x)} data-testid="share-x-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 text-white/80 text-xs hover:border-gold/40 hover:text-gold transition-colors">
          <Twitter className="w-3.5 h-3.5" /> X
        </button>
        <button onClick={nativeShare} data-testid="share-more-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 text-white/80 text-xs hover:border-gold/40 hover:text-gold transition-colors">
          <Share2 className="w-3.5 h-3.5" /> More
        </button>
      </div>
      {ref.subscribed > 0 && (
        <div className="mt-3 rounded-sm border border-gold/25 bg-gold/5 px-3 py-2 text-xs text-white/70" data-testid="ref-reward-status">
          {ref.free_months_earned > 0
            ? <>You've earned <span className="text-gold font-semibold">{ref.free_months_earned} free month{ref.free_months_earned > 1 ? "s" : ""}</span> so far.{" "}</>
            : null}
          <span className="text-white/50">{ref.next_reward_in} more paying member{ref.next_reward_in > 1 ? "s" : ""} = your next free month.</span>
        </div>
      )}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        <div className="rounded-sm border border-white/10 bg-charcoal/40 p-2"><div className="font-display text-lg font-bold text-white" data-testid="ref-signups">{ref.signups}</div><div className="label-mono text-white/40">Invited</div></div>
        <div className="rounded-sm border border-white/10 bg-charcoal/40 p-2"><div className="font-display text-lg font-bold text-green-400" data-testid="ref-subscribed">{ref.subscribed}</div><div className="label-mono text-white/40">Members</div></div>
        <div className="rounded-sm border border-gold/25 bg-gold/5 p-2"><div className="font-display text-lg font-bold text-gold" data-testid="ref-free-months">{ref.free_months_earned}</div><div className="label-mono text-white/40">Free mo.</div></div>
        <div className="rounded-sm border border-white/10 bg-charcoal/40 p-2"><div className="font-display text-lg font-bold text-white" data-testid="ref-points">{ref.points_earned}</div><div className="label-mono text-white/40">Points</div></div>
      </div>
    </section>
  );
}

function LeaderboardCard() {
  const [win, setWin] = useState("all");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/leaderboard?window=${win}`).then(({ data }) => setData(data)).catch(() => {}); }, [win]);
  const rankIcon = (r) => r === 1 ? <Crown className="w-3.5 h-3.5 text-gold" /> : r === 2 ? <Medal className="w-3.5 h-3.5 text-white/60" /> : r === 3 ? <Medal className="w-3.5 h-3.5 text-amber-600" /> : <span className="w-3.5 text-center text-xs font-mono text-white/40">{r}</span>;
  return (
    <section className="card-surface rounded-md p-5" data-testid="leaderboard-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-gold" /><h2 className="label-mono">Top Investigators</h2></div>
        <div className="flex items-center gap-1 rounded-sm border border-white/10 p-0.5">
          {["all", "week"].map((w) => (
            <button key={w} onClick={() => setWin(w)} data-testid={`lb-${w}`}
              className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase transition-colors ${win === w ? "bg-gold text-charcoal" : "text-white/50 hover:text-white"}`}>
              {w === "all" ? "All-time" : "This week"}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <p className="text-sm text-white/40">Loading…</p>
      ) : data.leaders.length === 0 ? (
        <p className="text-sm text-white/40">No ranked members yet. Post, comment and invite to climb the board!</p>
      ) : (
        <div className="space-y-1.5">
          {data.leaders.slice(0, 10).map((l) => (
            <div key={l.rank} data-testid={`lb-row-${l.rank}`} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-sm ${l.is_me ? "bg-gold/10 border border-gold/25" : ""}`}>
              <span className="shrink-0 flex items-center justify-center w-4">{rankIcon(l.rank)}</span>
              <Link to={l.user_id ? `/u/${l.user_id}` : "#"} className="flex-1 min-w-0 text-sm text-white/80 truncate hover:text-gold transition-colors" data-testid={`lb-name-${l.rank}`}>{l.name}{l.is_me && <span className="text-gold"> (you)</span>}</Link>
              <span className="shrink-0 text-xs font-mono text-gold">{l.points}</span>
            </div>
          ))}
        </div>
      )}
      {data?.me?.rank && !data.leaders.slice(0, 10).some((l) => l.is_me) && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2.5 px-2" data-testid="lb-my-rank">
          <span className="shrink-0 w-4 text-center text-xs font-mono text-white/40">{data.me.rank}</span>
          <span className="flex-1 text-sm text-gold truncate">You · {data.me.tier}</span>
          <span className="shrink-0 text-xs font-mono text-gold">{data.me.points}</span>
        </div>
      )}
    </section>
  );
}

function RecruitersCard() {
  const [win, setWin] = useState("all");
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/leaderboard/recruiters?window=${win}`).then(({ data }) => setData(data)).catch(() => {}); }, [win]);
  const rankIcon = (r) => r === 1 ? <Crown className="w-3.5 h-3.5 text-gold" /> : r === 2 ? <Medal className="w-3.5 h-3.5 text-white/60" /> : r === 3 ? <Medal className="w-3.5 h-3.5 text-amber-600" /> : <span className="w-3.5 text-center text-xs font-mono text-white/40">{r}</span>;
  const topTen = data?.leaders?.slice(0, 10) || [];
  return (
    <section className="card-surface rounded-md p-5" data-testid="recruiters-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-gold" /><h2 className="label-mono">Top Recruiters</h2></div>
        <div className="flex items-center gap-1 rounded-sm border border-white/10 p-0.5">
          {["all", "month"].map((w) => (
            <button key={w} onClick={() => setWin(w)} data-testid={`recruiters-${w}`}
              className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase transition-colors ${win === w ? "bg-gold text-charcoal" : "text-white/50 hover:text-white"}`}>
              {w === "all" ? "All-time" : "This month"}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-white/40 mb-3">Most paying members invited. Climb the board — refer 15 in a month for a free year.</p>
      {!data ? (
        <p className="text-sm text-white/40">Loading…</p>
      ) : topTen.length === 0 ? (
        <p className="text-sm text-white/40">No recruiters yet. Share your invite link to claim the #1 spot!</p>
      ) : (
        <div className="space-y-1.5">
          {topTen.map((l) => (
            <div key={l.rank} data-testid={`recruiter-row-${l.rank}`} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-sm ${l.is_me ? "bg-gold/10 border border-gold/25" : ""}`}>
              <span className="shrink-0 flex items-center justify-center w-4">{rankIcon(l.rank)}</span>
              <Link to={l.user_id ? `/u/${l.user_id}` : "#"} className="flex-1 min-w-0 text-sm text-white/80 truncate hover:text-gold transition-colors" data-testid={`recruiter-name-${l.rank}`}>{l.name}{l.is_me && <span className="text-gold"> (you)</span>}</Link>
              <span className="shrink-0 text-xs font-mono text-gold">{l.referrals} invited</span>
            </div>
          ))}
        </div>
      )}
      {data?.me?.rank && !topTen.some((l) => l.is_me) && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2.5 px-2" data-testid="recruiter-my-rank">
          <span className="shrink-0 w-4 text-center text-xs font-mono text-white/40">{data.me.rank}</span>
          <span className="flex-1 text-sm text-gold truncate">You</span>
          <span className="shrink-0 text-xs font-mono text-gold">{data.me.referrals} invited</span>
        </div>
      )}
    </section>
  );
}

function EmailIssueBanner() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  if (!user || user.email_status !== "bounced" || done) return null;
  const save = async () => {
    if (!email.trim()) { toast.error("Enter a new email"); return; }
    setSaving(true);
    try {
      await api.put("/me/email", { email: email.trim() });
      toast.success("Email updated — you'll get our emails now.");
      setDone(true);
      refreshUser?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-md p-5 mb-8 border border-red-500/40 bg-red-500/[0.06]" data-testid="email-issue-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-white">We couldn't deliver emails to you</h3>
          <p className="text-sm text-white/60 mt-1">Emails to <span className="text-white font-mono">{user.email}</span> are bouncing, so you're missing the weekly digest and updates. Update your email to fix it.</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 max-w-md">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="email-fix-input"
              className="flex-1 bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder-white/30 focus:border-gold/50 outline-none" />
            <button onClick={save} disabled={saving} data-testid="email-fix-save-btn"
              className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-gold text-charcoal text-sm font-semibold rounded-sm hover:bg-gold-hover transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AffiliateOnlyDashboard() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="label-mono mb-2">Affiliate Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]}.</h1>
          <p className="mt-3 text-white/50 leading-relaxed">Share your link and earn free membership. Every 2 people you invite who become paying members = 1 free month for you, automatically.</p>
        </div>

        <div className="rounded-lg p-6 mb-8 border border-gold/40 bg-gold/[0.06] grain" data-testid="limbo-join-prompt">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <UserPlus className="w-6 h-6 text-gold mt-0.5 shrink-0" />
              <div>
                <h3 className="font-display text-lg font-semibold text-white">You&apos;re registered — but not a member yet</h3>
                <p className="text-sm text-white/60 mt-1 max-w-xl">Your account is set up, but you don&apos;t have access to the case files, the Bullpen, or the new Homicide Investigations Course yet. Join to unlock everything — or enter a code if you have one.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Link to="/pricing" data-testid="limbo-join-btn" className="px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm inline-flex items-center gap-2">Join as a member <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/pricing" data-testid="limbo-redeem-btn" className="px-5 py-2.5 border border-white/15 text-white font-semibold rounded-sm hover:border-gold/50 transition-colors text-sm inline-flex items-center gap-2"><Gift className="w-4 h-4" /> Redeem a code</Link>
            </div>
          </div>
        </div>

        <EmailIssueBanner />
        <ReferralCard />
        <div className="mt-8"><RecruitersCard /></div>

        <div className="mt-8 card-surface rounded-md p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-gold/20" data-testid="affiliate-upsell">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-gold mt-0.5" />
            <div>
              <h3 className="font-display font-semibold text-white">Want the full Bullpen?</h3>
              <p className="text-sm text-white/50">Become a Founding Member for case files, the AI Assistant, Detective Notes and live meetings — or earn it free by referring 2 paying members.</p>
            </div>
          </div>
          <Link to="/pricing" data-testid="affiliate-subscribe-btn" className="shrink-0 px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">Subscribe from $4.99/mo</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [follows, setFollows] = useState([]);
  const [feedMax, setFeedMax] = useState(6);
  const [announcements, setAnnouncements] = useState([]);
  const [notif, setNotif] = useState([]);
  const [feed, setFeed] = useState([]);
  const [recent, setRecent] = useState([]);
  const [videos, setVideos] = useState([]);
  const [channelVids, setChannelVids] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    refreshUser?.();
    api.get("/me/follows").then(({ data }) => setFollows(data)).catch(() => {});
    api.get("/announcements").then(({ data }) => setAnnouncements(data)).catch(() => {});
    api.get("/me/notifications").then(({ data }) => setNotif(data.notifications)).catch(() => {});
    api.get("/me/feed").then(({ data }) => setFeed(data)).catch(() => {});
    api.get("/community/recent").then(({ data }) => setRecent(data.items || [])).catch(() => {});
    api.get("/videos/recent?limit=8").then(({ data }) => setVideos(data || [])).catch(() => {});
    api.get("/youtube/channel-latest").then(({ data }) => setChannelVids(data.videos || [])).catch(() => {});
    api.get("/me/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const isMember = user?.role === "admin" || user?.subscription_status === "active";
  const isAdmin = user?.role === "admin";

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not open the billing portal. Please try again shortly.");
      setPortalLoading(false);
    }
  };

  // Free (non-paying) accounts get the affiliate-only experience.
  if (!isMember && !isAdmin) return <AffiliateOnlyDashboard />;

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-mono mb-2">Command Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]}.</h1>
          </div>
          <Link to="/cases" data-testid="browse-cases-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">
            <FolderSearch className="w-4 h-4" /> Browse all cases
          </Link>
        </div>

        <EmailIssueBanner />

        {isAdmin && <StreamTodo />}

        {isAdmin && <GoLive />}

        <CaseSearchBar />

        {isAdmin && <AdminSignupNote />}

        {!isMember && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="card-surface rounded-md p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-gold/20">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-gold mt-0.5" />
              <div>
                <h3 className="font-display font-semibold text-white">Unlock full access</h3>
                <p className="text-sm text-white/50">Become a Founding Member to use the AI Assistant, post in discussions, and read Detective Notes.</p>
              </div>
            </div>
            <Link to="/pricing" className="shrink-0 px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">Subscribe from $4.99/mo</Link>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-10">
            {announcements.length > 0 && (
              <section data-testid="announcements-feed">
                <div className="flex items-center gap-2 mb-4"><Megaphone className="w-4 h-4 text-gold" /><h2 className="label-mono">Announcements from HSTC</h2></div>
                <div className="space-y-3" data-testid="announcements-feed-list">
                  {announcements.slice(0, 3).map((a) => (
                    <AnnouncementCard key={a.ann_id} a={a} user={user} />
                  ))}
                </div>
              </section>
            )}

            {isMember && <LatestVideos channelVids={channelVids} caseVids={videos} />}

            {isMember && (
              <section data-testid="recent-messages-section">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-gold" /><h2 className="label-mono">Latest in the Bullpen · Last 24h</h2></div>
                  {recent.some((m) => m.following) && <span className="label-mono text-white/40 flex items-center gap-1"><Star className="w-3 h-3 text-gold fill-current" /> follows</span>}
                </div>
                {recent.length === 0 ? (
                  <div className="card-surface rounded-md p-8 text-center text-white/40 text-sm">
                    No new messages in the last 24 hours. <Link to="/cases" className="text-gold">Browse cases →</Link>
                  </div>
                ) : (
                  <div className="card-surface rounded-md divide-y divide-white/5 max-h-[26rem] overflow-y-auto" data-testid="recent-messages-list">
                    {recent.map((m, i) => <MessageRow key={`${m.thread_id}-${m.type}-${m.created_at}-${i}`} m={m} />)}
                  </div>
                )}
              </section>
            )}

            <section>
              <div className="flex items-center gap-2 mb-4"><Rss className="w-4 h-4 text-gold" /><h2 className="label-mono">Your Feed · Updates From Cases You Follow</h2></div>
              {feed.length === 0 ? (
                <div className="card-surface rounded-md p-8 text-center text-white/40 text-sm">
                  Follow cases to see their latest documents, detective notes, and discussions here. <Link to="/cases" className="text-gold">Find a case →</Link>
                </div>
              ) : (
                <div className="card-surface rounded-md divide-y divide-white/5">
                  {feed.slice(0, feedMax).map((f, i) => <FeedItem key={`${f.type}-${f.link || f.case_slug}-${f.created_at}-${i}`} f={f} />)}
                  {feed.length > feedMax && (
                    <button onClick={() => setFeedMax(feedMax + 6)} data-testid="feed-show-more" className="w-full py-3 text-xs font-semibold text-gold hover:bg-white/5 transition-colors">
                      Show more ({feed.length - feedMax})
                    </button>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="space-y-8">
            {isMember && (
              <section className="card-surface rounded-md p-5" data-testid="stats-card">
                <div className="flex items-center gap-2 mb-4"><Trophy className="w-4 h-4 text-gold" /><h2 className="label-mono">Your Standing</h2></div>
                <div className="rounded-sm border border-gold/25 bg-gold/5 p-3 mb-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 text-gold"><Zap className="w-4 h-4" /><span className="font-display text-2xl font-bold text-white" data-testid="points-total">{stats?.points_total ?? 0}</span></div>
                    <div className="label-mono mt-1 text-white/40">Total points</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gold" data-testid="rep-tier">{stats?.tier ?? "Case Follower"}</div>
                    <div className="label-mono mt-1 text-white/40">Rank #{stats?.rank ?? "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-sm border border-white/10 bg-charcoal/40 p-3">
                    <div className="flex items-center gap-1 text-orange-400"><Flame className="w-4 h-4" /><span className="font-display text-xl font-bold text-white" data-testid="streak-count">{stats?.current_streak ?? 0}</span></div>
                    <div className="label-mono mt-1 text-white/40">Streak</div>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-charcoal/40 p-3">
                    <div className="flex items-center gap-1 text-gold"><Star className="w-4 h-4" /><span className="font-display text-xl font-bold text-white" data-testid="reputation-count">{stats?.reputation ?? 0}</span></div>
                    <div className="label-mono mt-1 text-white/40">Likes</div>
                  </div>
                  <div className="rounded-sm border border-white/10 bg-charcoal/40 p-3">
                    <div className="flex items-center gap-1 text-green-400"><MessagesSquare className="w-4 h-4" /><span className="font-display text-xl font-bold text-white">{(stats?.threads ?? 0) + (stats?.comments ?? 0)}</span></div>
                    <div className="label-mono mt-1 text-white/40">Posts</div>
                  </div>
                </div>
                {stats?.badges?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5" data-testid="badges-row">
                    {stats.badges.map((b) => {
                      const BIcon = BADGE_ICON[b.icon] || Award;
                      return (
                        <span key={b.id} data-testid={`badge-${b.id}`} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm bg-gold/10 border border-gold/25 text-gold">
                          <BIcon className="w-3 h-3" /> {b.label}
                        </span>
                      );
                    })}
                  </div>
                )}
                {(stats?.longest_streak ?? 0) > (stats?.current_streak ?? 0) && (
                  <p className="mt-3 text-xs text-white/40 font-mono">Best streak: {stats.longest_streak} days · keep it going!</p>
                )}
              </section>
            )}

            <ReferralCard />

            {isMember && <LeaderboardCard />}

            <RecruitersCard />

            {user?.role !== "admin" && user?.subscription_status === "active" && (
              <section className="card-surface rounded-md p-5" data-testid="membership-card">
                <div className="flex items-center gap-2 mb-4"><CreditCard className="w-4 h-4 text-gold" /><h2 className="label-mono">Membership</h2></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/70">Plan</span>
                  <span className="text-sm font-semibold text-white capitalize">{user?.subscription_plan === "annual" ? "Annual" : user?.subscription_plan === "monthly" ? "Monthly" : "Founding Member"}</span>
                </div>
                {user?.subscription_expires && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-white/70">{user?.subscription_cancel_at_period_end ? "Access until" : "Renews"}</span>
                    <span className="text-sm font-mono text-white/80">{new Date(user.subscription_expires).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                )}
                {user?.subscription_cancel_at_period_end && (
                  <div className="mt-3 rounded-sm border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-xs text-amber-300">
                    Your subscription is set to cancel and won&apos;t auto-renew.
                  </div>
                )}
                {user?.has_billing ? (
                  <button onClick={openPortal} disabled={portalLoading} data-testid="manage-subscription-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-white text-sm font-semibold rounded-sm hover:border-gold/40 transition-colors disabled:opacity-60">
                    {portalLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</> : <><CreditCard className="w-4 h-4" /> Manage / cancel subscription</>}
                  </button>
                ) : (
                  <p className="mt-4 text-xs text-white/40">Complimentary access — no billing on file.</p>
                )}
              </section>
            )}

            <section className="card-surface rounded-md p-5">
              <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-gold" /><h2 className="label-mono">Recent Activity</h2></div>
              <div className="space-y-3">
                {notif.length === 0 && <p className="text-sm text-white/40">Nothing yet.</p>}
                {notif.slice(0, 6).map((n, i) => (
                  <div key={n.notif_id || i} className="flex items-start gap-2 text-sm text-white/70">
                    <ArrowRight className="w-3.5 h-3.5 text-gold mt-1 shrink-0" /> {n.message}
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="cases-you-follow">
              <div className="flex items-center gap-2 mb-4"><Star className="w-4 h-4 text-gold" /><h2 className="label-mono">Cases You Follow</h2></div>
              {follows.length === 0 ? (
                <div className="card-surface rounded-md p-8 text-center text-white/40 text-sm">
                  You&apos;re not following any cases yet. <Link to="/cases" className="text-gold">Find a case →</Link>
                </div>
              ) : (
                <div className="flex flex-col gap-2" data-testid="follows-chip-list">
                  {[...follows].sort((a, b) => a.title.localeCompare(b.title)).map((c) => (
                    <Link key={c.case_id} to={`/cases/${c.slug}`} data-testid={`follow-chip-${c.case_id}`}
                      className="group flex items-center gap-3 card-surface rounded-md px-3 py-2.5 hover:border-gold/40 border border-transparent transition-colors">
                      <FolderSearch className="w-4 h-4 text-gold shrink-0" />
                      <span className="text-sm text-white/85 truncate flex-1 group-hover:text-white">{caseDisplayName(c.title)}</span>
                      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-gold shrink-0" />
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const FEED_META = {
  note: { icon: NotebookPen, label: "Detective Note", color: "#F5C518" },
  document: { icon: FileText, label: "Document", color: "#5B8DEF" },
  thread: { icon: MessagesSquare, label: "Discussion", color: "#34C759" },
  video: { icon: Video, label: "Video", color: "#FF4D4D" },
  timeline: { icon: Clock, label: "Timeline", color: "#B08CFF" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function FeedItem({ f }) {
  const meta = FEED_META[f.type] || FEED_META.note;
  const Icon = meta.icon;
  return (
    <Link to={f.link || "#"} data-testid="feed-item" className="flex items-start gap-3 p-4 hover:bg-white/5 transition-colors">
      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${meta.color}1A`, border: `1px solid ${meta.color}40` }}>
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-mono" style={{ color: meta.color }}>{meta.label}</span>
          <span className="text-xs text-white/30 font-mono">· {caseDisplayName(f.case_title)}</span>
          <span className="text-xs text-white/30 font-mono ml-auto">{timeAgo(f.created_at)}</span>
        </div>
        <h4 className="text-sm font-semibold text-white mt-0.5 truncate">{f.title}</h4>
        {f.snippet && <p className="text-xs text-white/50 mt-0.5 line-clamp-1">{f.snippet}</p>}
      </div>
    </Link>
  );
}
